import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2,
  X, RefreshCw, Package, Truck, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Split a CSV line respecting double-quoted values (handles commas in quoted cells)
const splitCSVLine = (line) => {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map(v => v.replace(/\r$/, '').trim());
};

const EXPECTED_HEADERS = [
  'product_sku', 'received_quantity', 'damaged_quantity', 'batch_number',
  'mfg_date', 'expiry_date', 'b2b_price_excl_gst', 'gst_percentage', 'mrp',
  'rack_no', 'bin_no'
];

export default function GRNBulkUploadPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');

  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(API + '/suppliers', { headers: { Authorization: 'Bearer ' + token } })
      .then(res => {
        const active = (res.data || []).filter(s => !s.status || s.status === 'active');
        setSuppliers(active);
      })
      .catch(() => toast.error('Failed to load suppliers'));
  }, []);

  const downloadTemplate = () => {
    const csv = `product_sku,received_quantity,damaged_quantity,batch_number,mfg_date,expiry_date,b2b_price_excl_gst,gst_percentage,mrp,rack_no,bin_no
PROD-000001,100,0,BATCH-A1,2025-01-15,2027-01-14,150.00,18,199.00,R-A1,B-01
PROD-000002,50,2,,,,,,,R-A2,B-05
PROD-000003,25,0,BATCH-B1,,2026-06-30,,12,,,`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'grn_bulk_upload_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded');
  };

  const parseCSV = useCallback((text) => {
    const lines = text.replace(/^\uFEFF/, '').trim().split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length < 2) {
      toast.error('CSV file must have a header row and at least one data row');
      return [];
    }
    const headers = splitCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = splitCSVLine(lines[i]);
      if (values.every(v => !v)) continue;
      const row = { row_number: i + 1 };
      headers.forEach((h, idx) => {
        const raw = values[idx] !== undefined ? values[idx] : '';
        if (h === 'received_quantity' || h === 'damaged_quantity' || h === 'b2b_price_excl_gst' || h === 'gst_percentage' || h === 'mrp') {
          const num = parseFloat(raw);
          row[h] = isNaN(num) ? (h === 'damaged_quantity' ? 0 : null) : num;
        } else {
          row[h] = raw || null;
        }
      });
      // required defaults
      if (row.damaged_quantity === null || row.damaged_quantity === undefined) row.damaged_quantity = 0;
      data.push(row);
    }
    return data;
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFile(f);
    setValidationResult(null);
  };

  const handleValidate = async () => {
    if (!supplierId) { toast.error('Please select a supplier first'); return; }
    if (!file) { toast.error('Please choose a CSV file'); return; }
    setParsing(true);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length === 0) { setParsing(false); return; }
      setParsing(false);
      setValidating(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        API + '/grn/bulk-validate',
        { supplier_id: supplierId, items: rows },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setValidationResult(res.data);
      if (res.data.valid_rows > 0 && res.data.error_rows === 0) {
        toast.success(`All ${res.data.valid_rows} rows validated successfully`);
      } else if (res.data.valid_rows > 0) {
        toast.warning(`${res.data.valid_rows} valid, ${res.data.error_rows} rows have errors`);
      } else {
        toast.error(`No valid rows. ${res.data.error_rows} errors found.`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Validation failed');
    } finally {
      setParsing(false);
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!validationResult || validationResult.valid_rows === 0) {
      toast.error('No valid rows to import');
      return;
    }
    const validItems = (validationResult.validated_items || []).filter(it => it.is_valid);
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        API + '/grn/bulk-import',
        {
          supplier_id: supplierId,
          supplier_name: supplierName,
          received_date: receivedDate,
          invoice_number: invoiceNumber || null,
          invoice_date: invoiceDate || null,
          notes: notes || null,
          items: validItems,
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      toast.success(`Bulk GRN ${res.data.grn_number} created with ${res.data.items_imported} items`);
      navigate('/grn-dashboard');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorCsv = () => {
    if (!validationResult) return;
    const errorRows = (validationResult.validated_items || []).filter(r => !r.is_valid);
    if (errorRows.length === 0) { toast.info('No error rows to download'); return; }
    const header = 'row_number,product_sku,received_quantity,damaged_quantity,error';
    const body = errorRows.map(r => [
      r.row_number,
      JSON.stringify(r.product_sku || ''),
      r.received_quantity || 0,
      r.damaged_quantity || 0,
      JSON.stringify(r.error || '')
    ].join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'grn_bulk_errors.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Error CSV downloaded');
  };

  const validItemsList = useMemo(
    () => (validationResult?.validated_items || []).filter(r => r.is_valid),
    [validationResult]
  );
  const errorItemsList = useMemo(
    () => (validationResult?.validated_items || []).filter(r => !r.is_valid),
    [validationResult]
  );

  const totalValue = useMemo(
    () => validItemsList.reduce((sum, r) => sum + (r.line_total || 0), 0),
    [validItemsList]
  );

  return (
    <div className="space-y-6" data-testid="grn-bulk-upload-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/grn-dashboard')} className="gap-1" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-primary">Bulk GRN Upload</h1>
            <p className="text-slate-600 text-sm">Create a Manual GRN by uploading a CSV of received items</p>
          </div>
        </div>
        <Badge className="bg-purple-100 text-purple-800">BULK ENTRY</Badge>
      </div>

      {/* Supplier & Meta */}
      <Card className="border-purple-200 bg-purple-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Truck className="w-5 h-5 text-purple-600" /> GRN Header Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium">Supplier *</Label>
              <Select
                value={supplierId}
                onValueChange={(val) => {
                  setSupplierId(val);
                  const sup = suppliers.find(s => s.id === val);
                  setSupplierName(sup ? sup.name : '');
                  setValidationResult(null);
                }}
              >
                <SelectTrigger data-testid="bulk-grn-supplier-select">
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Received Date *</Label>
              <Input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                data-testid="bulk-grn-received-date"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Invoice Number (Optional)</Label>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Supplier invoice #"
                data-testid="bulk-grn-invoice-number"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Invoice Date (Optional)</Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any remarks..."
                className="h-[38px] min-h-[38px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" /> Upload CSV
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-medium text-sm">CSV columns (in order):</p>
                <code className="text-xs text-slate-600 block mt-1">
                  {EXPECTED_HEADERS.join(', ')}
                </code>
                <p className="text-xs text-slate-500 mt-2">
                  <b>product_sku</b> and <b>received_quantity</b> are mandatory.
                  Price / GST fall back to Master Catalogue when left blank.
                  If <b>mrp</b> is provided, it updates the product MRP.
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-template-btn">
                <Download className="w-4 h-4" /> Download Template
              </Button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-md cursor-pointer hover:bg-blue-50 transition-colors">
              <Upload className="w-4 h-4 text-blue-600" />
              <span className="text-sm">{file ? file.name : 'Choose CSV File'}</span>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                data-testid="bulk-grn-file-input"
              />
            </label>
            {file && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFile(null); setValidationResult(null); }}
                className="text-red-600"
                data-testid="bulk-grn-clear-file"
              >
                <X className="w-4 h-4" /> Clear
              </Button>
            )}
            <Button
              onClick={handleValidate}
              disabled={!file || !supplierId || parsing || validating}
              className="gap-2"
              data-testid="bulk-grn-validate-btn"
            >
              {parsing || validating
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <CheckCircle2 className="w-4 h-4" />}
              {parsing ? 'Parsing...' : validating ? 'Validating...' : 'Validate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Validation Results */}
      {validationResult && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" /> Validation Results
              </span>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700" data-testid="bulk-grn-valid-count">
                  {validationResult.valid_rows} valid
                </Badge>
                {validationResult.error_rows > 0 && (
                  <Badge className="bg-red-100 text-red-700" data-testid="bulk-grn-error-count">
                    {validationResult.error_rows} errors
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 rounded-lg bg-slate-50 border text-center">
                <p className="text-xs text-slate-500 uppercase">Total Rows</p>
                <p className="text-xl font-bold">{validationResult.total_rows}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                <p className="text-xs text-emerald-600 uppercase">Valid</p>
                <p className="text-xl font-bold text-emerald-700">{validationResult.valid_rows}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                <p className="text-xs text-amber-700 uppercase">Est. Value (Incl GST)</p>
                <p className="text-xl font-bold text-amber-800">₹{totalValue.toFixed(2)}</p>
              </div>
            </div>

            {/* Valid rows table */}
            {validItemsList.length > 0 && (
              <div className="border rounded-lg overflow-x-auto mb-4">
                <table className="w-full text-xs min-w-[1100px]" data-testid="bulk-grn-valid-table">
                  <thead className="bg-emerald-50">
                    <tr>
                      <th className="px-2 py-2 text-left">Row</th>
                      <th className="px-2 py-2 text-left">SKU</th>
                      <th className="px-2 py-2 text-left">Product</th>
                      <th className="px-2 py-2 text-right">Received</th>
                      <th className="px-2 py-2 text-right">Damaged</th>
                      <th className="px-2 py-2 text-right">Accepted</th>
                      <th className="px-2 py-2 text-right">B2B (Excl)</th>
                      <th className="px-2 py-2 text-right">GST%</th>
                      <th className="px-2 py-2 text-right">MRP</th>
                      <th className="px-2 py-2 text-center bg-violet-50">Rack</th>
                      <th className="px-2 py-2 text-center bg-violet-50">Bin</th>
                      <th className="px-2 py-2 text-right">Line Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {validItemsList.map((r) => (
                      <tr key={r.row_number} className="hover:bg-emerald-50/50">
                        <td className="px-2 py-1.5">{r.row_number}</td>
                        <td className="px-2 py-1.5 font-mono">{r.product_sku}</td>
                        <td className="px-2 py-1.5">{r.product_name}</td>
                        <td className="px-2 py-1.5 text-right">{r.received_quantity}</td>
                        <td className="px-2 py-1.5 text-right">{r.damaged_quantity}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-emerald-700">{r.accepted_quantity}</td>
                        <td className="px-2 py-1.5 text-right">₹{(r.b2b_price_excl_gst || 0).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right">{r.gst_percentage}%</td>
                        <td className="px-2 py-1.5 text-right">{r.mrp ? '₹' + Number(r.mrp).toFixed(2) : '-'}</td>
                        <td className="px-2 py-1.5 text-center bg-violet-50/30">{r.rack_no || '-'}</td>
                        <td className="px-2 py-1.5 text-center bg-violet-50/30">{r.bin_no || '-'}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-amber-700">₹{(r.line_total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Error rows */}
            {errorItemsList.length > 0 && (
              <div className="border border-red-200 bg-red-50/40 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-semibold text-sm">
                      {errorItemsList.length} row(s) have errors and will be skipped
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowErrors(v => !v)}
                      data-testid="bulk-grn-toggle-errors"
                    >
                      {showErrors ? 'Hide' : 'Show'} Errors
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadErrorCsv}
                      className="gap-2"
                      data-testid="bulk-grn-download-errors"
                    >
                      <Download className="w-4 h-4" /> Download Error CSV
                    </Button>
                  </div>
                </div>
                {showErrors && (
                  <div className="border rounded bg-white overflow-x-auto">
                    <table className="w-full text-xs" data-testid="bulk-grn-error-table">
                      <thead className="bg-red-100 text-red-800">
                        <tr>
                          <th className="px-2 py-2 text-left">Row</th>
                          <th className="px-2 py-2 text-left">SKU</th>
                          <th className="px-2 py-2 text-right">Received Qty</th>
                          <th className="px-2 py-2 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {errorItemsList.map((r) => (
                          <tr key={'err-' + r.row_number}>
                            <td className="px-2 py-1.5">{r.row_number}</td>
                            <td className="px-2 py-1.5 font-mono">{r.product_sku || '-'}</td>
                            <td className="px-2 py-1.5 text-right">{r.received_quantity}</td>
                            <td className="px-2 py-1.5 text-red-700">{r.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/grn-dashboard')} data-testid="bulk-grn-cancel">
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          disabled={importing || !validationResult || validationResult.valid_rows === 0}
          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          data-testid="bulk-grn-import-btn"
        >
          {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
          {importing
            ? 'Creating GRN...'
            : `Create GRN with ${validationResult?.valid_rows || 0} Items`}
        </Button>
      </div>
    </div>
  );
}
